import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Building2, MapPin, Euro, ArrowRight, ArrowLeft, Trash2, X, Loader2, FileText, CheckCircle2, AlertCircle } from 'lucide-react';
import { Deal, DealStatus } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useDealsQuery, useAddDealMutation, useUpdateDealMutation, useDeleteDealMutation } from '../hooks/useAppQuery';

// Form Validation
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { dealSchema, DealFormData } from '../lib/schemas';

// Helpers
const MotionDiv = motion.div as any;

const DealsManager: React.FC = () => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Data Hooks
  const { data: deals = [], isLoading } = useDealsQuery(user?.id);
  const addMutation = useAddDealMutation(user?.id);
  const updateMutation = useUpdateDealMutation(user?.id);
  const deleteMutation = useDeleteDealMutation(user?.id);

  // Form
  const { register, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm<DealFormData>({
    resolver: zodResolver(dealSchema) as any,
    defaultValues: { status: DealStatus.New, askingPrice: 0, estRent: 0, estRenovation: 0 }
  });

  const handleOpenModal = (deal?: Deal) => {
    if (deal) {
      setEditingId(deal.id);
      setValue('address', deal.address);
      setValue('city', deal.city);
      setValue('askingPrice', deal.askingPrice);
      setValue('offerPrice', deal.offerPrice);
      setValue('estRent', deal.estRent);
      setValue('estRenovation', deal.estRenovation);
      setValue('status', deal.status);
      setValue('imageUrl', deal.imageUrl || '');
      setValue('notes', deal.notes);
    } else {
      setEditingId(null);
      reset();
    }
    setIsModalOpen(true);
  };

  const onSubmit = async (data: DealFormData) => {
    try {
      if (editingId) {
        // Find original to keep dateAdded and id
        const original = deals.find(d => d.id === editingId);
        if (!original) return;
        await updateMutation.mutateAsync({ ...original, ...data });
      } else {
        await addMutation.mutateAsync(data as any);
      }
      setIsModalOpen(false);
      reset();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this deal?')) await deleteMutation.mutateAsync(id);
  };

  const moveStage = async (deal: Deal, direction: 'next' | 'prev') => {
    const stages = Object.values(DealStatus);
    const currentIndex = stages.indexOf(deal.status);
    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    
    if (newIndex >= 0 && newIndex < stages.length) {
      await updateMutation.mutateAsync({ ...deal, status: stages[newIndex] });
    }
  };

  if (isLoading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-emerald-500 w-8 h-8"/></div>;

  // Kanban Columns Configuration
  const columns = [
    { id: DealStatus.New, label: 'New Leads', color: 'border-blue-500/50' },
    { id: DealStatus.Analyzing, label: 'Analyzing', color: 'border-yellow-500/50' },
    { id: DealStatus.OfferSent, label: 'Offer Sent', color: 'border-purple-500/50' },
    { id: DealStatus.UnderContract, label: 'Under Contract', color: 'border-emerald-500/50' },
    { id: DealStatus.Closed, label: 'Closed', color: 'border-slate-500/50' },
  ];

  return (
    <div className="p-6 h-[calc(100vh-6rem)] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 shrink-0">
        <div>
          <h2 className="text-3xl font-bold text-white tracking-tight">Deal Flow</h2>
          <p className="text-slate-400 mt-1">Pipeline value: <span className="text-emerald-400 font-mono">€{deals.reduce((acc, d) => acc + d.askingPrice, 0).toLocaleString()}</span></p>
        </div>
        <button onClick={() => handleOpenModal()} className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-emerald-500/20 transition-all active:scale-95">
          <Plus className="w-5 h-5" /> New Deal
        </button>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden pb-4">
        <div className="flex gap-4 h-full min-w-[1200px]">
          {columns.map(col => {
            const colDeals = deals.filter(d => d.status === col.id);
            return (
              <div key={col.id} className="w-80 flex flex-col h-full bg-slate-900/30 rounded-2xl border border-slate-800/50 backdrop-blur-sm">
                {/* Column Header */}
                <div className={`p-4 border-b border-slate-800 flex justify-between items-center ${col.id === DealStatus.Closed ? 'opacity-60' : ''}`}>
                  <span className="font-bold text-slate-300 text-sm uppercase tracking-wider">{col.label}</span>
                  <span className="bg-slate-800 text-slate-400 px-2 py-0.5 rounded text-xs font-mono">{colDeals.length}</span>
                </div>
                
                {/* Cards Container */}
                <div className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin scrollbar-thumb-slate-700">
                  <AnimatePresence mode="popLayout">
                    {colDeals.map(deal => (
                      <MotionDiv
                        key={deal.id}
                        layoutId={deal.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className={`bg-slate-800 border ${col.color} rounded-xl p-4 shadow-lg group hover:border-emerald-500/50 transition-all cursor-pointer relative`}
                        onClick={() => handleOpenModal(deal)}
                      >
                        {/* Image Preview */}
                        {deal.imageUrl && (
                          <div className="h-24 w-full rounded-lg bg-slate-900 mb-3 overflow-hidden relative">
                            <img src={deal.imageUrl} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                          </div>
                        )}

                        <div className="mb-2">
                          <h4 className="font-bold text-white text-sm leading-tight">{deal.address}</h4>
                          <div className="flex items-center gap-1 text-slate-400 text-xs mt-1">
                            <MapPin className="w-3 h-3" /> {deal.city}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 my-3">
                          <div className="bg-slate-900/50 p-1.5 rounded border border-slate-700/50 text-center">
                            <p className="text-[10px] text-slate-500 uppercase">Ask</p>
                            <p className="text-xs font-mono text-white">€{deal.askingPrice.toLocaleString()}</p>
                          </div>
                          <div className="bg-slate-900/50 p-1.5 rounded border border-slate-700/50 text-center">
                            <p className="text-[10px] text-slate-500 uppercase">Rent</p>
                            <p className="text-xs font-mono text-emerald-400">€{deal.estRent}</p>
                          </div>
                        </div>

                        {/* Actions Overlay */}
                        <div className="flex justify-between items-center pt-2 border-t border-slate-700/50 mt-2" onClick={(e) => e.stopPropagation()}>
                          <button 
                            onClick={() => moveStage(deal, 'prev')}
                            disabled={deal.status === DealStatus.New}
                            className="p-1.5 rounded hover:bg-slate-700 text-slate-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowLeft className="w-4 h-4" />
                          </button>
                          
                          <button onClick={() => handleDelete(deal.id)} className="text-slate-600 hover:text-red-400 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>

                          <button 
                            onClick={() => moveStage(deal, 'next')}
                            disabled={deal.status === DealStatus.Closed}
                            className="p-1.5 rounded hover:bg-emerald-500/20 text-slate-500 hover:text-emerald-400 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </MotionDiv>
                    ))}
                  </AnimatePresence>
                  {colDeals.length === 0 && (
                    <div className="h-20 border-2 border-dashed border-slate-800 rounded-xl flex items-center justify-center text-slate-600 text-xs uppercase tracking-widest">Empty</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal Form */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <MotionDiv 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-slate-950 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
            >
              <div className="sticky top-0 bg-slate-950 border-b border-slate-800 p-6 flex justify-between items-center z-10">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                   <FileText className="w-5 h-5 text-emerald-500"/>
                   {editingId ? 'Edit Deal' : 'New Opportunity'}
                </h3>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-500 hover:text-white"><X className="w-6 h-6"/></button>
              </div>
              
              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Address *</label>
                      <input {...register('address')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none" />
                      {errors.address && <span className="text-xs text-red-400">{errors.address.message}</span>}
                   </div>
                   <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">City *</label>
                      <input {...register('city')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none" />
                      {errors.city && <span className="text-xs text-red-400">{errors.city.message}</span>}
                   </div>
                </div>

                <div className="grid grid-cols-2 gap-4 bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-slate-500 uppercase">Asking Price (€)</label>
                      <input type="number" {...register('askingPrice')} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-emerald-500/70 uppercase">Est. Rent (€)</label>
                      <input type="number" {...register('estRent')} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-blue-500/70 uppercase">Offer Price (€)</label>
                      <input type="number" {...register('offerPrice')} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white text-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-red-500/70 uppercase">Renovation Cost (€)</label>
                      <input type="number" {...register('estRenovation')} className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-white text-sm" />
                    </div>
                </div>

                <div className="space-y-1.5">
                   <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Status</label>
                   <select {...register('status')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none">
                      {Object.values(DealStatus).map(s => <option key={s} value={s}>{s}</option>)}
                   </select>
                </div>

                <div className="space-y-1.5">
                   <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Image URL</label>
                   <input {...register('imageUrl')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none" placeholder="https://..." />
                </div>

                <div className="space-y-1.5">
                   <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Notes</label>
                   <textarea {...register('notes')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none h-24" placeholder="Analysis notes..." />
                </div>

                <div className="pt-4 flex justify-end gap-3 border-t border-slate-800">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
                    <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50">
                        {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
                        Save Deal
                    </button>
                </div>
              </form>
            </MotionDiv>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DealsManager;